import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { getOsAnalyticsExport, getOsDetail } from '../../../Service/os'
import { downloadBase64File, downloadCsv } from '../../../utils/download-file'
import { useOsAnalytics } from '../../../hooks/os'
import {
  OsAnalyticsCategorias,
  OsAnalyticsEvento,
  OsAnalyticsFilters,
  OsAnalyticsRankingItem,
  OsDetail,
  OsEquipamentoTipo,
} from '../../../types/os'
import { HBarItem, HBars, LineChart, StackedBar } from '../../Bordero/charts'
import {
  BD,
  Button,
  Card,
  Field,
  FiltersRow,
  Input,
  KpiCard,
  KpiGrid,
  PageSubtitle,
  PageTitle,
  Select,
  Spinner,
  StatusBadge,
  Table,
} from '../styles'
import { fmtDate, fmtMoney, STATUS_UI } from '../statusUi'

/* ── loading animado (igual ao Borderô: logo girando + frases) ── */
const BD_MARK = `${process.env.PUBLIC_URL || ''}/favicon.svg`
const flip = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`
const LoadBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 12px;
`
const SpinLogo = styled.img`
  width: 56px;
  height: 56px;
  animation: ${flip} 1.6s ease-in-out infinite;
`
const LoadMsg = styled.div`
  font-size: 14px;
  font-weight: 560;
  color: #616161;
  min-height: 20px;
`
const LOADING_MSGS = [
  'Acordando os técnicos…',
  'Contando leitores um por um…',
  'Calculando horas extras (sem chorar)…',
  'Rodando os quilômetros…',
  'Pagando a alimentação do turno…',
  'Caçando equipamentos para retorno…',
  'Cobrando quem não devolveu o carregador…',
  'Somando cachês e pedágios…',
  'Importando tudo pro P2…',
  'Fechando a OS antes do produtor ligar…',
]
const AnalyticsLoading: FC = () => {
  const [i, setI] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % LOADING_MSGS.length), 1400)
    return () => clearInterval(t)
  }, [])
  return (
    <LoadBox>
      <SpinLogo src={BD_MARK} alt="Carregando" />
      <LoadMsg>{LOADING_MSGS[i]}</LoadMsg>
    </LoadBox>
  )
}

/* ── formatação ─────────────────────────────────────────────── */
const compactBRL = (v: number): string => {
  const n = Number(v) || 0
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(2).replace('.', ',')} mi`
  if (n >= 1e3) return `R$ ${Math.round(n / 1e3)} mil`
  return fmtMoney(n)
}
const fmtInt = (n: number): string => (Number(n) || 0).toLocaleString('pt-BR')
/** Decimal curto (até 2 casas) — para médias pequenas não virarem "0". */
const fmtDec = (n: number): string =>
  (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
/** 'YYYY-MM' -> 'mmm/AA' */
const fmtMes = (m: string): string => {
  const [ano, mes] = m.split('-')
  const idx = Number(mes) - 1
  return MESES[idx] ? `${MESES[idx]}/${ano.slice(2)}` : m
}

/* ── categorias de custo (ordem fixa; cores das séries do Borderô) ── */
const CATEGORIAS: Array<{ key: keyof OsAnalyticsCategorias; label: string; color: string }> = [
  { key: 'tecnicos', label: 'Técnicos', color: '#2A3B4C' },
  { key: 'equipamentos', label: 'Equipamentos', color: '#4CAF50' },
  { key: 'deslocamento', label: 'Deslocamento', color: '#FFC107' },
  { key: 'logistica', label: 'Logística', color: '#FF4C3E' },
  { key: 'hospedagem', label: 'Hospedagem', color: '#616161' },
  { key: 'alimentacao', label: 'Alimentação', color: '#52708D' },
  { key: 'staff', label: 'Staff', color: '#98A2B3' },
]

const TIPOS_RETORNO: OsEquipamentoTipo[] = [
  'LEITOR',
  'SMART',
  'NETBOOK',
  'NOTEBOOK',
  'TEF',
  'EPSON',
  'ZEBRA',
  'CELULAR',
  'CARREGADOR',
]

/* ── períodos rápidos ───────────────────────────────────────── */
const fmtISO = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const PRESETS: Array<{ key: string; label: string; range: () => { dataIni: string; dataFim: string } }> = [
  { key: 'hoje', label: 'Hoje', range: () => ({ dataIni: fmtISO(new Date()), dataFim: fmtISO(new Date()) }) },
  {
    key: 'd7',
    label: '7 dias',
    range: () => {
      const fim = new Date()
      const ini = new Date()
      ini.setDate(fim.getDate() - 6)
      return { dataIni: fmtISO(ini), dataFim: fmtISO(fim) }
    },
  },
  {
    key: 'd30',
    label: '30 dias',
    range: () => {
      const fim = new Date()
      const ini = new Date()
      ini.setDate(fim.getDate() - 29)
      return { dataIni: fmtISO(ini), dataFim: fmtISO(fim) }
    },
  },
  {
    key: 'mes',
    label: 'Mês atual',
    range: () => {
      const hoje = new Date()
      return { dataIni: fmtISO(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), dataFim: fmtISO(hoje) }
    },
  },
  {
    key: 'mesAnt',
    label: 'Mês anterior',
    range: () => {
      const hoje = new Date()
      return {
        dataIni: fmtISO(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
        dataFim: fmtISO(new Date(hoje.getFullYear(), hoje.getMonth(), 0)),
      }
    },
  },
  {
    key: 'ano',
    label: 'Ano atual',
    range: () => {
      const hoje = new Date()
      return { dataIni: fmtISO(new Date(hoje.getFullYear(), 0, 1)), dataFim: fmtISO(hoje) }
    },
  },
]

const FILTERS_KEY = 'os-analytics-filters'
const loadFilters = (): OsAnalyticsFilters | null => {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY)
    return raw ? (JSON.parse(raw) as OsAnalyticsFilters) : null
  } catch {
    return null
  }
}

/* ── ajuda ("?") com tooltip explicando o cálculo ───────────── */
const HelpWrap = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: 1px;
  margin-left: 6px;
  cursor: help;
  &:hover .help-tip {
    opacity: 1;
    visibility: visible;
  }
`
const HelpDot = styled.span`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  color: #9ca3af;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`
const HelpBubble = styled.span`
  position: absolute;
  z-index: 50;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% + 8px);
  width: 270px;
  background: #101828;
  color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.24);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s;
  pointer-events: none;
`
const HelpTip: FC<{ text: React.ReactNode }> = ({ text }) => (
  <HelpWrap>
    <HelpDot>?</HelpDot>
    <HelpBubble className="help-tip">{text}</HelpBubble>
  </HelpWrap>
)

/* ── pequenos blocos de UI ──────────────────────────────────── */
const StatLine: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 10,
      fontSize: 13,
      padding: '7px 0',
      borderBottom: `1px solid ${BD.grayBg}`,
    }}
  >
    <span style={{ color: BD.grayText }}>{label}</span>
    <span style={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
)

const PanelTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{children}</div>
)

const SectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 15,
      fontWeight: 700,
      margin: '28px 0 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}
  >
    {children}
  </div>
)

const Toggle: FC<{
  options: Array<{ key: string; label: string }>
  value: string
  onChange: (v: string) => void
}> = ({ options, value, onChange }) => (
  <span
    style={{
      display: 'inline-flex',
      gap: 4,
      background: BD.grayBg,
      border: `1px solid ${BD.grayBorder}`,
      borderRadius: 9,
      padding: 3,
    }}
  >
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        onClick={() => onChange(o.key)}
        style={{
          border: 0,
          background: value === o.key ? '#fff' : 'transparent',
          color: value === o.key ? BD.black : BD.grayText,
          boxShadow: value === o.key ? '0 1px 2px rgba(16,24,40,.1)' : 'none',
          fontSize: 12.5,
          fontWeight: 600,
          padding: '5px 12px',
          borderRadius: 6,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {o.label}
      </button>
    ))}
  </span>
)

/* ── drill de lançamentos (getOsDetail sob demanda) ─────────── */
const DrillGroup: FC<{ title: string; total?: number | null; children: React.ReactNode }> = ({
  title,
  total,
  children,
}) => (
  <div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#9CA3AF',
        fontWeight: 700,
        borderBottom: `1px dashed ${BD.grayBorder}`,
        paddingBottom: 5,
        marginBottom: 6,
      }}
    >
      <span>{title}</span>
      {total != null && <span style={{ color: BD.black, letterSpacing: 0 }}>{fmtMoney(total)}</span>}
    </div>
    {children}
  </div>
)

const DrillLine: FC<{ desc: string; meta?: string; valor?: number | string | null }> = ({ desc, meta, valor }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
    <span style={{ color: BD.grayText, minWidth: 0 }}>
      {desc}
      {meta && <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>{meta}</span>}
    </span>
    {valor != null && (
      <span style={{ fontWeight: 650, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(valor)}</span>
    )}
  </div>
)

const OsDrill: FC<{ detail: OsDetail }> = ({ detail }) => {
  const c = detail.custos ?? {}
  const somaEquipe = detail.equipe.reduce((s, e) => s + (Number(e.cacheCalculado) || 0), 0)
  const somaAlim = detail.equipe.reduce((s, e) => s + (Number(e.alimentacao) || 0), 0)
  const somaEquip = detail.equipamentos.reduce((s, e) => s + (Number(e.qtd) || 0) * (Number(e.valorUnit) || 0), 0)
  const somaLog = detail.custoLogistica.reduce((s, l) => s + (Number(l.valor) || 0), 0)
  return (
    <div
      style={{
        background: BD.grayBg,
        border: `1px solid ${BD.grayBorder}`,
        borderRadius: 10,
        padding: '14px 16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '14px 24px',
      }}
    >
      <DrillGroup title="Técnicos (equipe)" total={somaEquipe}>
        {detail.equipe.length === 0 && <DrillLine desc="Sem técnicos lançados." />}
        {detail.equipe.map((e, i) => (
          <DrillLine
            key={e.id ?? i}
            desc={e.emailSnapshot ?? `Técnico #${e.tecnicoId}`}
            meta={`${e.turnos.length} turno(s)${Number(e.alimentacao) ? ` · alim. ${fmtMoney(e.alimentacao ?? 0)}` : ''}`}
            valor={e.cacheCalculado ?? 0}
          />
        ))}
        {somaAlim > 0 && <DrillLine desc="Alimentação da equipe (total)" valor={somaAlim} />}
      </DrillGroup>

      <DrillGroup title="Deslocamento e custos diretos">
        {Number(c.kmRodado) > 0 && <DrillLine desc="Km rodado" meta={`${fmtInt(Number(c.kmRodado))} km × tarifa da config`} />}
        {Number(c.combustivel) > 0 && <DrillLine desc="Combustível" valor={c.combustivel} />}
        {Number(c.pedagio) > 0 && <DrillLine desc="Pedágio" valor={c.pedagio} />}
        {Number(c.uber) > 0 && <DrillLine desc="Uber" valor={c.uber} />}
        {Number(c.estacionamento) > 0 && <DrillLine desc="Estacionamento" valor={c.estacionamento} />}
        {Number(c.aluguelCarro) > 0 && <DrillLine desc="Aluguel de carro" valor={c.aluguelCarro} />}
        {Number(c.hospedagem) > 0 && <DrillLine desc="Hospedagem" valor={c.hospedagem} />}
        {Number(c.staff) > 0 && <DrillLine desc="Staff" valor={c.staff} />}
        {![c.kmRodado, c.combustivel, c.pedagio, c.uber, c.estacionamento, c.aluguelCarro, c.hospedagem, c.staff].some(
          (v) => Number(v) > 0,
        ) && <DrillLine desc="Sem custos diretos lançados." />}
      </DrillGroup>

      <DrillGroup title="Leitores por setor">
        {detail.leitorSetores.length === 0 && <DrillLine desc="Sem setores lançados." />}
        {detail.leitorSetores.map((s, i) => (
          <DrillLine
            key={s.id ?? i}
            desc={s.setor}
            meta={`${fmtInt(s.emitidos)} emitidos · sug. ${s.leitoresSugeridos ?? 0} · util. ${s.leitoresUtilizados ?? 0}${s.origem ? ` · ${s.origem}` : ''}`}
          />
        ))}
        {detail.maquinas.length > 0 && (
          <DrillLine desc={`Máquinas: ${detail.maquinas.map((m) => m.tipo).join(', ')}`} />
        )}
      </DrillGroup>

      <DrillGroup title="Equipamentos p/ retorno" total={somaEquip}>
        {detail.equipamentos.length === 0 && <DrillLine desc="Sem equipamentos lançados." />}
        {detail.equipamentos.map((e, i) => (
          <DrillLine
            key={e.id ?? i}
            desc={`${e.tipo} × ${e.qtd}`}
            meta={`${fmtMoney(e.valorUnit)}/un (snapshot)`}
            valor={(Number(e.qtd) || 0) * (Number(e.valorUnit) || 0)}
          />
        ))}
      </DrillGroup>

      <DrillGroup title="Logística" total={somaLog}>
        {detail.custoLogistica.length === 0 && <DrillLine desc="Sem lançamentos." />}
        {detail.custoLogistica.map((l) => (
          <DrillLine key={l.id} desc={l.descricao} meta={`${fmtDate(l.data)}${l.usuarioNome ? ` · ${l.usuarioNome}` : ''}`} valor={l.valor} />
        ))}
        {detail.anexos.length > 0 && <DrillLine desc={`${detail.anexos.length} comprovante(s) anexado(s)`} />}
      </DrillGroup>
    </div>
  )
}

/* ── página ─────────────────────────────────────────────────── */
const EVENTOS_PAGE_SIZE = 20

export const OsAnalytics: FC = () => {
  const inicial = useMemo<OsAnalyticsFilters>(() => loadFilters() ?? { ...PRESETS[5].range(), dateField: 'evento' }, [])
  const [draft, setDraft] = useState<OsAnalyticsFilters>(inicial)
  const [applied, setApplied] = useState<OsAnalyticsFilters>(inicial)
  const { analytics, pracas, isLoading, isError } = useOsAnalytics(applied)

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(applied))
    } catch {
      /* sessionStorage indisponível — ignora */
    }
  }, [applied])

  const [rankMetric, setRankMetric] = useState('custo')
  const [evolMode, setEvolMode] = useState('mensal')
  const [retTab, setRetTab] = useState('graficos')
  const [retMetric, setRetMetric] = useState('qtd')
  const [eventosPage, setEventosPage] = useState(1)
  const [tecnicosPage, setTecnicosPage] = useState(1)
  const [exportando, setExportando] = useState(false)
  const [openOs, setOpenOs] = useState<Set<number>>(new Set())
  const [drills, setDrills] = useState<Record<number, OsDetail | 'loading' | 'error'>>({})

  const setD = (key: keyof OsAnalyticsFilters, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value || undefined }))
  const aplicar = () => {
    setApplied({ ...draft })
    setEventosPage(1)
  }
  const limpar = () => {
    const base: OsAnalyticsFilters = { ...PRESETS[5].range(), dateField: 'evento' }
    setDraft(base)
    setApplied(base)
    setEventosPage(1)
  }
  const aplicarPreset = (key: string) => {
    const preset = PRESETS.find((p) => p.key === key)
    if (!preset) return
    const range = preset.range()
    setDraft((d) => ({ ...d, ...range }))
    setApplied((a) => ({ ...a, ...range }))
    setEventosPage(1)
  }
  const presetAtivo = useMemo(
    () => PRESETS.find((p) => {
      const r = p.range()
      return r.dataIni === applied.dataIni && r.dataFim === applied.dataFim
    })?.key,
    [applied.dataIni, applied.dataFim],
  )

  // Lista completa vinda da API (o ranking só traz praças do período filtrado).
  // O fallback pelo ranking cobre o intervalo até o backend com `pracas` estar no ar.
  const pracaOptions = useMemo(
    () =>
      pracas.length
        ? pracas.map((p) => [p.id, p.nome] as const)
        : (analytics?.rankings.pracas ?? []).filter((p) => p.id != null).map((p) => [p.id as number, p.nome] as const),
    [pracas, analytics],
  )

  // Evolução mensal: pontos do gráfico (mensal ou acumulado) com tooltip de valores reais.
  const evolPoints = useMemo(() => {
    let acumulado = 0
    return (analytics?.evolucaoMensal ?? []).map((m) => {
      acumulado += m.custoTotal
      return {
        x: fmtMes(m.mes),
        y: evolMode === 'acumulado' ? acumulado : m.custoTotal,
        tipLines: [
          { label: 'Custo no mês', value: fmtMoney(m.custoTotal) },
          ...(evolMode === 'acumulado' ? [{ label: 'Acumulado', value: fmtMoney(acumulado) }] : []),
          { label: 'OS no mês', value: fmtInt(m.qtdOs) },
        ],
      }
    })
  }, [analytics, evolMode])

  const toggleDrill = (osId: number) => {
    setOpenOs((prev) => {
      const next = new Set(prev)
      if (next.has(osId)) next.delete(osId)
      else next.add(osId)
      return next
    })
    if (!drills[osId]) {
      setDrills((d) => ({ ...d, [osId]: 'loading' }))
      getOsDetail(osId)
        .then((res) => setDrills((d) => ({ ...d, [osId]: res.data?.data as OsDetail })))
        .catch(() => setDrills((d) => ({ ...d, [osId]: 'error' })))
    }
  }

  /* rankings: valor exibido conforme a métrica escolhida */
  const rankItems = (items: OsAnalyticsRankingItem[]): HBarItem[] =>
    items
      .map((i) => {
        const value =
          rankMetric === 'tecnicos' ? i.tecnicos : rankMetric === 'equip' ? i.equipamentos : i.custoTotal
        return {
          label: i.nome,
          value,
          valueLabel: rankMetric === 'custo' ? compactBRL(value) : fmtInt(value),
          sub:
            rankMetric === 'custo'
              ? `${i.qtdOs} OS · Ø ${compactBRL(i.custoMedio)}/OS`
              : `${i.qtdOs} OS · ${compactBRL(i.custoTotal)}`,
        }
      })
      .sort((a, b) => b.value - a.value)

  /* retorno: ranking por praça/produtor na métrica qtd|valor */
  const retornoRank = (items: OsAnalyticsRankingItem[]): HBarItem[] =>
    items
      .filter((i) => i.retornoQtd > 0 || i.retornoValor > 0)
      .map((i) => {
        const value = retMetric === 'qtd' ? i.retornoQtd : i.retornoValor
        return {
          label: i.nome,
          value,
          valueLabel: retMetric === 'qtd' ? fmtInt(value) : fmtMoney(value),
          sub: retMetric === 'qtd' ? fmtMoney(i.retornoValor) : `${fmtInt(i.retornoQtd)} un.`,
        }
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)

  const exportRetorno = () => {
    if (!analytics) return
    const header = ['Praça', 'Evento', 'Produtor', 'Data evento', 'Nº OS']
    TIPOS_RETORNO.forEach((t) => header.push(`${t} (qtd)`, `${t} (R$)`))
    header.push('Total (qtd)', 'Total (R$)')
    const rows = [header]
    analytics.retorno.porOs.forEach((os) => {
      const row = [os.praca ?? '', os.evento ?? '', os.produtor ?? '', fmtDate(os.dataEvento), String(os.numero)]
      TIPOS_RETORNO.forEach((t) => {
        const item = os.tipos[t]
        row.push(item ? String(item.qtd) : '', item ? item.valor.toFixed(2).replace('.', ',') : '')
      })
      row.push(String(os.totalQtd), os.totalValor.toFixed(2).replace('.', ','))
      rows.push(row)
    })
    downloadCsv(rows, `equipamentos-retorno_${applied.dataIni ?? ''}_${applied.dataFim ?? ''}.csv`)
  }

  const exportEventos = () => {
    if (!analytics) return
    const rows = [
      ['Nº OS', 'Evento', 'Produtor', 'Praça', 'Data', 'Técnicos (R$)', 'Staff (R$)', 'Alimentação (R$)', 'Deslocamento (R$)', 'Hospedagem (R$)', 'Equipamentos (R$)', 'Logística (R$)', 'Custo total (R$)', 'Status'],
      ...analytics.eventos.map((e) => [
        String(e.numero),
        e.evento ?? '',
        e.produtor ?? '',
        e.praca ?? '',
        fmtDate(e.dataEvento),
        e.categorias.tecnicos.toFixed(2).replace('.', ','),
        e.categorias.staff.toFixed(2).replace('.', ','),
        e.categorias.alimentacao.toFixed(2).replace('.', ','),
        e.categorias.deslocamento.toFixed(2).replace('.', ','),
        e.categorias.hospedagem.toFixed(2).replace('.', ','),
        e.categorias.equipamentos.toFixed(2).replace('.', ','),
        e.categorias.logistica.toFixed(2).replace('.', ','),
        e.custoTotal.toFixed(2).replace('.', ','),
        STATUS_UI[e.status]?.label ?? e.status,
      ]),
    ]
    downloadCsv(rows, `analytics-operacoes_${applied.dataIni ?? ''}_${applied.dataFim ?? ''}.csv`)
  }

  const exportExcel = async () => {
    setExportando(true)
    try {
      const res = await getOsAnalyticsExport(applied)
      const body = res.data
      downloadBase64File(body.fileBase64, body.fileName, body.mimeType)
    } catch {
      window.alert('Não foi possível gerar o Excel. Tente novamente.')
    } finally {
      setExportando(false)
    }
  }

  const exportTecnicos = () => {
    if (!analytics) return
    const rows = [
      ['Técnico', 'Eventos', 'Locais', 'Viagem', 'A definir', 'Turnos', 'Horas', 'Cachê total (R$)', 'Cachê médio/evento (R$)', 'Valor/hora (R$)', 'Alimentação (R$)'],
      ...analytics.tecnicos.map((t) => [
        t.nome,
        String(t.eventos),
        String(t.eventosLocais),
        String(t.eventosViagem),
        String(t.eventosADefinir),
        String(t.turnos),
        String(t.horas).replace('.', ','),
        t.cacheTotal.toFixed(2).replace('.', ','),
        t.cacheMedioPorEvento.toFixed(2).replace('.', ','),
        t.valorHora != null ? t.valorHora.toFixed(2).replace('.', ',') : '',
        t.alimentacao.toFixed(2).replace('.', ','),
      ]),
    ]
    downloadCsv(rows, `tecnicos_${applied.dataIni ?? ''}_${applied.dataFim ?? ''}.csv`)
  }

  const TECNICOS_PAGE_SIZE = 15
  const tecnicosPageRows = useMemo(() => {
    const all = analytics?.tecnicos ?? []
    return all.slice((tecnicosPage - 1) * TECNICOS_PAGE_SIZE, tecnicosPage * TECNICOS_PAGE_SIZE)
  }, [analytics, tecnicosPage])
  const tecnicosLastPage = Math.max(1, Math.ceil((analytics?.tecnicos.length ?? 0) / TECNICOS_PAGE_SIZE))

  const eventosPageRows = useMemo<OsAnalyticsEvento[]>(() => {
    const all = analytics?.eventos ?? []
    return all.slice((eventosPage - 1) * EVENTOS_PAGE_SIZE, eventosPage * EVENTOS_PAGE_SIZE)
  }, [analytics, eventosPage])
  const eventosLastPage = Math.max(1, Math.ceil((analytics?.eventos.length ?? 0) / EVENTOS_PAGE_SIZE))

  const k = analytics?.kpis
  const cont = analytics?.contadores

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Analytics de Operações</PageTitle>
          <PageSubtitle>Visão gerencial das Ordens de Serviço — custos, equipes, equipamentos e praças</PageSubtitle>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button $variant="secondary" onClick={exportEventos} disabled={!analytics}>
            Exportar CSV
          </Button>
          <Button $variant="secondary" onClick={exportExcel} disabled={!analytics || exportando}>
            {exportando ? 'Gerando…' : '⇩ Exportar Excel'}
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: BD.grayText }}>Período:</span>
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            $variant="secondary"
            style={{
              padding: '6px 14px',
              fontSize: 13,
              ...(presetAtivo === p.key ? { background: BD.black, borderColor: BD.black, color: '#fff' } : {}),
            }}
            onClick={() => aplicarPreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <FiltersRow as="form" onSubmit={(e: any) => { e.preventDefault(); aplicar() }}>
        <Field>
          <label>Data inicial</label>
          <Input type="date" value={draft.dataIni ?? ''} onChange={(e) => setD('dataIni', e.target.value)} />
        </Field>
        <Field>
          <label>Data final</label>
          <Input type="date" value={draft.dataFim ?? ''} onChange={(e) => setD('dataFim', e.target.value)} />
        </Field>
        <Field>
          <label>Data de referência</label>
          <Select value={draft.dateField ?? 'evento'} onChange={(e) => setD('dateField', e.target.value)}>
            <option value="evento">Data do evento</option>
            <option value="criacao">Criação da OS</option>
            <option value="aprovacao">Aprovação</option>
            <option value="importacao">Importação P2</option>
          </Select>
        </Field>
        <Field>
          <label>Praça</label>
          <Select value={draft.praca ?? ''} onChange={(e) => setD('praca', e.target.value)}>
            <option value="">Todas</option>
            {pracaOptions.map(([id, nome]) => (
              <option key={id} value={id}>{nome}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <label>Status</label>
          <Select value={draft.status ?? ''} onChange={(e) => setD('status', e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(STATUS_UI).map(([key, ui]) => (
              <option key={key} value={key}>{ui.label}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <label>Importação P2</label>
          <Select value={draft.importStatus ?? ''} onChange={(e) => setD('importStatus', e.target.value)}>
            <option value="">Todas</option>
            <option value="PENDENTE">Pendente</option>
            <option value="PARCIAL">Parcial</option>
            <option value="IMPORTADA">Importada</option>
          </Select>
        </Field>
        <Field>
          <label>Tipo de Evento</label>
          <Select value={draft.tipo ?? ''} onChange={(e) => setD('tipo', e.target.value)}>
            <option value="">Todos</option>
            <option value="LOCAL">Local</option>
            <option value="VIAGEM">Viagem</option>
          </Select>
        </Field>
        <Field>
          <label>Atendimento</label>
          <Select value={draft.atendimento ?? ''} onChange={(e) => setD('atendimento', e.target.value)}>
            <option value="">Todos</option>
            <option value="1">Com atendimento</option>
            <option value="0">Sem atendimento</option>
          </Select>
        </Field>
        <Field>
          <label>Produtor</label>
          <Input value={draft.produtor ?? ''} onChange={(e) => setD('produtor', e.target.value)} placeholder="Buscar..." />
        </Field>
        <Field>
          <label>Nome do Evento</label>
          <Input value={draft.evento ?? ''} onChange={(e) => setD('evento', e.target.value)} placeholder="Buscar..." />
        </Field>
        <Field>
          <label>Origem dos leitores</label>
          <Select value={draft.origem ?? ''} onChange={(e) => setD('origem', e.target.value)}>
            <option value="">Todas</option>
            <option value="BD">BD</option>
            <option value="OLC">OLC</option>
            <option value="ROSEMBERG">ROSEMBERG</option>
          </Select>
        </Field>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Button type="submit">Filtrar</Button>
          <Button type="button" $variant="secondary" onClick={limpar}>Limpar</Button>
        </div>
      </FiltersRow>

      {isLoading && (
        <Card>
          <AnalyticsLoading />
        </Card>
      )}
      {!isLoading && isError && (
        <Card style={{ textAlign: 'center', padding: 40, color: '#991B1B' }}>Erro ao carregar. Tente novamente.</Card>
      )}

      {!isLoading && !isError && analytics && k && cont && (
        <>
          {/* KPIs */}
          <KpiGrid>
            <KpiCard style={{ borderColor: BD.red }}>
              <div className="kpi-label">
                Custo operacional
                <HelpTip text="Soma do custo total de todas as OS do filtro, pelas mesmas regras do resumo da OS: cachês de técnicos + staff + alimentação + deslocamento + hospedagem + equipamentos de retorno + logística. OS marcadas como 'não atende' somam apenas equipamentos + logística." />
              </div>
              <div className="kpi-value" style={{ color: BD.red }}>{compactBRL(k.custoTotal)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                média {fmtMoney(k.custoMedioPorOs)}/OS{k.custoPorIngresso != null ? ` · ${fmtMoney(k.custoPorIngresso)}/ingresso` : ''}
              </div>
            </KpiCard>
            <KpiCard>
              <div className="kpi-label">
                Ordens de Serviço
                <HelpTip text="Quantidade de OS no filtro (cada evento tem 1 OS). Produtores e praças são contagens distintas dentro dessas OS." />
              </div>
              <div className="kpi-value">{fmtInt(k.totalOs)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                {fmtInt(k.produtores)} produtores · {fmtInt(k.pracas)} praças
              </div>
            </KpiCard>
            <KpiCard>
              <div className="kpi-label">
                Ingressos emitidos
                <HelpTip text="Soma dos ingressos emitidos registrados nos setores de cada OS (dado importado da bilheteria). É a base dos indicadores 'por ingresso'." />
              </div>
              <div className="kpi-value">{fmtInt(k.emitidos)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                média {fmtInt(k.totalOs ? Math.round(k.emitidos / k.totalOs) : 0)} por evento
              </div>
            </KpiCard>
            <KpiCard>
              <div className="kpi-label">
                Técnicos alocados
                <HelpTip text="Alocações = técnicos escalados somando todas as OS (o mesmo técnico em 2 eventos conta 2). Profissionais = técnicos distintos. Média por evento = alocações ÷ total de OS." />
              </div>
              <div className="kpi-value">{fmtInt(k.tecnicosAlocacoes)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                {fmtInt(k.tecnicosDistintos)} profissionais · média {fmtDec(k.totalOs ? k.tecnicosAlocacoes / k.totalOs : 0)}/evento
              </div>
            </KpiCard>
            <KpiCard>
              <div className="kpi-label">
                Equipamentos
                <HelpTip text="Leitores utilizados + máquinas de bilheteria + equipamentos para retorno, somados em todas as OS do filtro." />
              </div>
              <div className="kpi-value">{fmtInt(k.equipamentosTotal)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                {fmtInt(k.leitoresUtilizados)} leitores · {fmtInt(k.maquinas)} máquinas · {fmtInt(k.equipamentosRetorno)} retorno
              </div>
            </KpiCard>
            <KpiCard>
              <div className="kpi-label">
                Importação P2
                <HelpTip text="OS com custos já importados para o fechamento financeiro (P2). 'Aprovadas a importar' = aprovadas com importação pendente; 'parciais' = importação parcial antes da aprovação." />
              </div>
              <div className="kpi-value">{fmtInt(cont.importacao.importadas)}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                {fmtInt(cont.importacao.aprovadasAImportar)} aprovadas a importar · {fmtInt(cont.importacao.parciais)} parciais
              </div>
            </KpiCard>
          </KpiGrid>

          {/* Composição dos custos */}
          <SectionTitle>
            Composição dos custos
            <HelpTip text="Participação de cada categoria no custo total do período. Deslocamento = km rodado × tarifa da configuração (congelada quando a OS é aprovada) + combustível, pedágio, uber, estacionamento e aluguel de carro. OS sem atendimento presencial não somam custos operacionais." />
          </SectionTitle>
          <Card>
            <StackedBar
              items={CATEGORIAS.map((cat) => ({
                label: cat.label,
                value: analytics.custos.categorias[cat.key],
                color: cat.color,
              }))}
            />
          </Card>

          {/* Evolução + tipo/atendimento */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 16, marginTop: 16 }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>
                  Evolução mensal de custos
                  <HelpTip text="Custo total das OS agrupado pelo mês da data de referência escolhida no filtro (padrão: data do evento). No modo 'Acumulado', cada ponto soma todos os meses anteriores. Passe o mouse nos pontos para ver os valores exatos." />
                </span>
                <Toggle
                  options={[{ key: 'mensal', label: 'Mensal' }, { key: 'acumulado', label: 'Acumulado' }]}
                  value={evolMode}
                  onChange={setEvolMode}
                />
              </div>
              <LineChart points={evolPoints} format={compactBRL} />
            </Card>
            <Card>
              <PanelTitle>
                Tipo de evento e atendimento
                <HelpTip text="Contagem de OS por tipo (local × viagem) e por atendimento presencial (sim × não). 'A definir' = o campo ainda não foi preenchido na OS. Km rodados = soma do km lançado nas OS que contam custo operacional." />
              </PanelTitle>
              <StackedBar
                format={(n) => `${fmtInt(n)} OS`}
                items={[
                  { label: 'Local', value: cont.tipo.local, color: '#2A3B4C' },
                  { label: 'Viagem', value: cont.tipo.viagem, color: '#FFC107' },
                  { label: 'A definir', value: cont.tipo.aDefinir, color: '#E0E0E0' },
                ]}
              />
              <div style={{ height: 12 }} />
              <StackedBar
                format={(n) => `${fmtInt(n)} OS`}
                items={[
                  { label: 'Com atendimento', value: cont.atendimento.com, color: '#4CAF50' },
                  { label: 'Sem atendimento', value: cont.atendimento.sem, color: '#616161' },
                  { label: 'A definir', value: cont.atendimento.aDefinir, color: '#E0E0E0' },
                ]}
              />
              <div style={{ borderTop: `1px dashed ${BD.grayBorder}`, marginTop: 14, paddingTop: 6 }}>
                <StatLine label="Km rodados no período" value={`${fmtInt(analytics.deslocamento.kmTotal)} km`} />
                <StatLine label="OS sem custos lançados" value={fmtInt(k.osSemCusto)} />
              </div>
            </Card>
          </div>

          {/* Equipe e equipamentos */}
          <SectionTitle>Equipe e equipamentos</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <Card>
              <PanelTitle>
                Equipe técnica
                <HelpTip text="Valor total = soma dos cachês calculados pelos turnos de cada técnico (piso + horas extras, pela configuração vigente na OS). Média por alocação = valor total ÷ técnicos escalados (quanto custa, em média, colocar um técnico num evento). Staff é o valor lançado na OS — não é quantidade de pessoas." />
              </PanelTitle>
              <StatLine label="Valor total pago" value={fmtMoney(analytics.equipe.valorTotal)} />
              <StatLine label="Média por alocação" value={fmtMoney(analytics.equipe.valorMedioPorAlocacao)} />
              <StatLine
                label="Média de técnicos por evento"
                value={fmtDec(k.totalOs ? k.tecnicosAlocacoes / k.totalOs : 0)}
              />
              <StatLine
                label="Ingressos por técnico"
                value={k.tecnicosAlocacoes ? fmtInt(Math.round(k.emitidos / k.tecnicosAlocacoes)) : '—'}
              />
              <StatLine label="Staff (valor)" value={fmtMoney(analytics.equipe.staffValor)} />
            </Card>
            <Card>
              <PanelTitle>
                Leitores: sugeridos × utilizados
                <HelpTip text="Sugeridos = cálculo automático por setor: ingressos emitidos ÷ divisor da configuração, arredondado para cima. Utilizados = quantidade informada na OS. A diferença mostra eventos operando acima ou abaixo da sugestão do sistema." />
              </PanelTitle>
              <HBars
                items={[
                  { label: 'Sugeridos', value: analytics.leitores.sugeridos, valueLabel: fmtInt(analytics.leitores.sugeridos) },
                  {
                    label: 'Utilizados',
                    value: analytics.leitores.utilizados,
                    valueLabel: fmtInt(analytics.leitores.utilizados),
                    sub:
                      analytics.leitores.diferencaPct != null
                        ? `${analytics.leitores.diferenca > 0 ? '+' : ''}${fmtInt(analytics.leitores.diferenca)} vs sugerido (${analytics.leitores.diferencaPct}%)`
                        : undefined,
                  },
                ]}
              />
              <div style={{ borderTop: `1px dashed ${BD.grayBorder}`, marginTop: 10, paddingTop: 4 }}>
                <StatLine
                  label="Média de leitores por evento"
                  value={fmtDec(k.totalOs ? analytics.leitores.utilizados / k.totalOs : 0)}
                />
                <StatLine
                  label="Ingressos por leitor"
                  value={analytics.leitores.ingressosPorLeitor != null ? fmtInt(analytics.leitores.ingressosPorLeitor) : '—'}
                />
              </div>
            </Card>
            <Card>
              <PanelTitle>
                Origem dos leitores
                <HelpTip text="Leitores utilizados somados pela origem informada em cada setor da OS: BD (frota própria), OLC e ROSEMBERG (parceiros, cobrados no fechamento P2)." />
              </PanelTitle>
              <HBars
                items={analytics.leitores.porOrigem.map((o) => ({
                  label: o.origem === 'NAO_INFORMADA' ? 'Não informada' : o.origem,
                  value: o.leitores,
                  valueLabel: fmtInt(o.leitores),
                }))}
              />
            </Card>
            <Card>
              <PanelTitle>
                Deslocamento e logística
                <HelpTip text="Deslocamento = km × tarifa da configuração + combustível, pedágio, uber, estacionamento e aluguel de carro. Hospedagem e alimentação somam no custo operacional, mas fora do deslocamento. Logística = soma das linhas de custo de envio/retorno de equipamentos lançadas nas OS." />
              </PanelTitle>
              <StatLine label="Custo de deslocamento" value={fmtMoney(analytics.deslocamento.total)} />
              <StatLine label="Hospedagem" value={fmtMoney(analytics.deslocamento.hospedagem)} />
              <StatLine label="Alimentação" value={fmtMoney(analytics.deslocamento.alimentacao)} />
              <StatLine label="Custo logístico (envios)" value={fmtMoney(analytics.logistica.total)} />
              <StatLine label="Média de logística por OS" value={fmtMoney(analytics.logistica.mediaPorOs)} />
            </Card>
          </div>

          {/* Indicadores por técnico */}
          <SectionTitle>
            Indicadores por técnico
            <HelpTip text="Um técnico por linha, somando as OS do filtro. Eventos = OS em que foi escalado; Locais/Viagem/A definir seguem o tipo do evento na OS. Horas = soma dos minutos dos turnos ÷ 60. Cachê total = soma dos cachês calculados (piso + horas extras). Valor/hora = cachê total ÷ horas." />
            <span style={{ marginLeft: 'auto' }}>
              <Button $variant="secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={exportTecnicos}>
                ⇩ Exportar CSV
              </Button>
            </span>
          </SectionTitle>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <th>Técnico</th>
                    <th style={{ textAlign: 'right' }}>Eventos</th>
                    <th style={{ textAlign: 'right' }}>Locais</th>
                    <th style={{ textAlign: 'right' }}>Viagem</th>
                    <th style={{ textAlign: 'right' }}>A definir</th>
                    <th style={{ textAlign: 'right' }}>Turnos</th>
                    <th style={{ textAlign: 'right' }}>Horas</th>
                    <th style={{ textAlign: 'right' }}>Cachê total</th>
                    <th style={{ textAlign: 'right' }}>Cachê médio/evento</th>
                    <th style={{ textAlign: 'right' }}>Valor/hora</th>
                    <th style={{ textAlign: 'right' }}>Alimentação</th>
                  </tr>
                </thead>
                <tbody>
                  {tecnicosPageRows.length === 0 && (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>Nenhum técnico alocado no período.</td></tr>
                  )}
                  {tecnicosPageRows.map((t) => (
                    <tr key={t.tecnicoId}>
                      <td style={{ fontWeight: 500 }}>{t.nome}</td>
                      <td style={{ textAlign: 'right', fontWeight: 650 }}>{fmtInt(t.eventos)}</td>
                      <td style={{ textAlign: 'right' }}>{t.eventosLocais ? fmtInt(t.eventosLocais) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{t.eventosViagem ? fmtInt(t.eventosViagem) : '—'}</td>
                      <td style={{ textAlign: 'right', color: BD.grayText }}>{t.eventosADefinir ? fmtInt(t.eventosADefinir) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtInt(t.turnos)}</td>
                      <td style={{ textAlign: 'right' }}>{t.horas ? `${fmtDec(t.horas)} h` : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 650 }}>{fmtMoney(t.cacheTotal)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(t.cacheMedioPorEvento)}</td>
                      <td style={{ textAlign: 'right' }}>{t.valorHora != null ? fmtMoney(t.valorHora) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{t.alimentacao ? fmtMoney(t.alimentacao) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 13, color: BD.grayText }}>
              {fmtInt(analytics.tecnicos.length)} técnicos · ordenado por eventos
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button $variant="secondary" disabled={tecnicosPage <= 1} onClick={() => setTecnicosPage((p) => p - 1)}>
                ‹ Anterior
              </Button>
              <span style={{ fontSize: 13, color: BD.grayText }}>Página {tecnicosPage} de {tecnicosLastPage}</span>
              <Button
                $variant="secondary"
                disabled={tecnicosPage >= tecnicosLastPage}
                onClick={() => setTecnicosPage((p) => p + 1)}
              >
                Próxima ›
              </Button>
            </div>
          </div>

          {/* Equipamentos para retorno */}
          <SectionTitle>
            Equipamentos para retorno
            <HelpTip text="Equipamentos lançados nas OS para retenção/retorno (leitores, smarts, notebooks, TEF etc.). Valor de retenção = quantidade × valor unitário congelado na OS (snapshot da configuração). A confirmação do retorno efetivado ainda não é rastreada pelo sistema." />
            <Toggle
              options={[{ key: 'graficos', label: 'Gráficos' }, { key: 'listagem', label: 'Listagem' }]}
              value={retTab}
              onChange={setRetTab}
            />
            {retTab === 'graficos' && (
              <Toggle
                options={[{ key: 'qtd', label: 'Quantidade' }, { key: 'valor', label: 'Valor' }]}
                value={retMetric}
                onChange={setRetMetric}
              />
            )}
            <span style={{ marginLeft: 'auto' }}>
              <Button $variant="secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={exportRetorno}>
                ⇩ Exportar Excel
              </Button>
            </span>
          </SectionTitle>

          {retTab === 'graficos' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              <Card>
                <PanelTitle>
                  Resumo
                  <HelpTip text="Total = soma das quantidades lançadas. Valor total = Σ quantidade × valor unitário (snapshot). Média por OS considera apenas as OS que têm equipamentos de retorno." />
                </PanelTitle>
                <StatLine label="Total de equipamentos" value={fmtInt(analytics.retorno.totalQtd)} />
                <StatLine label="Valor total (retenção)" value={fmtMoney(analytics.retorno.totalValor)} />
                <StatLine label="Média de valor por equipamento" value={fmtMoney(analytics.retorno.valorMedioPorEquipamento)} />
                <StatLine label="OS com equip. de retorno" value={fmtInt(analytics.retorno.osComRetorno)} />
                <StatLine label="Média de equipamentos por OS" value={fmtDec(analytics.retorno.mediaPorOs)} />
              </Card>
              <Card>
                <PanelTitle>
                  Por tipo
                  <HelpTip text="Quantidade (ou valor de retenção, conforme o toggle) somada por tipo de equipamento em todas as OS do filtro." />
                </PanelTitle>
                <HBars
                  items={[...analytics.retorno.porTipo]
                    .sort((a, b) => (retMetric === 'qtd' ? b.qtd - a.qtd : b.valor - a.valor))
                    .map((t) => ({
                      label: t.tipo,
                      value: retMetric === 'qtd' ? t.qtd : t.valor,
                      valueLabel: retMetric === 'qtd' ? fmtInt(t.qtd) : fmtMoney(t.valor),
                      sub: retMetric === 'qtd' ? fmtMoney(t.valor) : `${fmtInt(t.qtd)} un.`,
                    }))}
                />
              </Card>
              <Card>
                <PanelTitle>
                  Top praças
                  <HelpTip text="Praças com mais equipamentos de retorno (quantidade ou valor, conforme o toggle). Top 5." />
                </PanelTitle>
                <HBars items={retornoRank(analytics.rankings.pracas)} />
              </Card>
              <Card>
                <PanelTitle>
                  Top produtores
                  <HelpTip text="Produtores com mais equipamentos de retorno (quantidade ou valor, conforme o toggle). Top 5." />
                </PanelTitle>
                <HBars items={retornoRank(analytics.rankings.produtores)} />
              </Card>
            </div>
          )}

          {retTab === 'listagem' && (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Praça</th><th>Evento</th><th>Data</th><th>Nº OS</th>
                      {TIPOS_RETORNO.map((t) => (
                        <th key={t} style={{ textAlign: 'right' }}>{t === 'CARREGADOR' ? 'CARREG.' : t}</th>
                      ))}
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Total R$</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.retorno.porOs.length === 0 && (
                      <tr><td colSpan={15} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>Sem equipamentos de retorno no período.</td></tr>
                    )}
                    {analytics.retorno.porOs.map((os) => (
                      <tr key={os.osId}>
                        <td>{os.praca ?? '—'}</td>
                        <td style={{ fontWeight: 500 }}>
                          {os.evento ?? '—'}
                          <div style={{ fontSize: 11, color: '#9CA3AF' }}>{os.produtor ?? '—'}</div>
                        </td>
                        <td>{fmtDate(os.dataEvento)}</td>
                        <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{os.numero}</span></td>
                        {TIPOS_RETORNO.map((t) => {
                          const item = os.tipos[t]
                          return (
                            <td key={t} style={{ textAlign: 'right' }} title={item ? `${t}: ${item.qtd} un. · ${fmtMoney(item.valor)}` : undefined}>
                              {item ? <strong>{item.qtd}</strong> : <span style={{ color: '#E5E7EB' }}>—</span>}
                            </td>
                          )
                        })}
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{os.totalQtd}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(os.totalValor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div style={{ padding: '10px 14px', fontSize: 12.5, color: BD.grayText }}>
                {fmtInt(analytics.retorno.porOs.length)} OS com equipamentos · passe o mouse na quantidade para ver o valor
              </div>
            </Card>
          )}

          {/* Rankings */}
          <SectionTitle>
            Rankings
            <HelpTip text="Top 10 por custo total (mesma regra do custo operacional; 'Outros' agrega o restante). O toggle troca a métrica exibida: técnicos alocados ou equipamentos utilizados (leitores + máquinas + retorno). A linha menor mostra a quantidade de OS e o custo médio." />
            <Toggle
              options={[
                { key: 'custo', label: 'Custo total' },
                { key: 'tecnicos', label: 'Técnicos' },
                { key: 'equip', label: 'Equipamentos' },
              ]}
              value={rankMetric}
              onChange={setRankMetric}
            />
          </SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <Card>
              <PanelTitle>Top praças</PanelTitle>
              <HBars items={rankItems(analytics.rankings.pracas)} />
            </Card>
            <Card>
              <PanelTitle>Top eventos</PanelTitle>
              <HBars items={rankItems(analytics.rankings.eventos)} />
            </Card>
            <Card>
              <PanelTitle>Top produtores</PanelTitle>
              <HBars items={rankItems(analytics.rankings.produtores)} />
            </Card>
          </div>

          {/* Importação P2 e eficiência */}
          <SectionTitle>Importação P2 e eficiência</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <Card>
              <PanelTitle>
                Importação P2
                <HelpTip text="Valores efetivamente lançados no fechamento (P2), rastreados item a item. As visões produtor e empresa recebem lançamentos separados — por isso NUNCA são somadas. Pendente = custo total das OS aprovadas que ainda não foram importadas." />
              </PanelTitle>
              <StatLine label="Importado — visão produtor" value={fmtMoney(analytics.importacaoP2.importadoProdutor)} />
              <StatLine label="Importado — visão empresa" value={fmtMoney(analytics.importacaoP2.importadoEmpresa)} />
              <StatLine label="Pendente de importação" value={fmtMoney(analytics.importacaoP2.pendenteImportacao)} />
              <StatLine label="OS importadas" value={fmtInt(cont.importacao.importadas)} />
              <StatLine label="Importações parciais" value={fmtInt(cont.importacao.parciais)} />
            </Card>
            <Card>
              <PanelTitle>
                Tempos do fluxo
                <HelpTip text="Médias entre as datas de auditoria da OS: criação → aprovação e aprovação → importação P2, em dias. Considera apenas OS que já passaram por cada etapa. Abaixo, o histórico de OS importadas por mês (pela data da importação)." />
              </PanelTitle>
              <StatLine
                label="Criação → aprovação"
                value={analytics.importacaoP2.tempoMedioAprovacaoDias != null ? `${fmtDec(analytics.importacaoP2.tempoMedioAprovacaoDias)} dias` : '—'}
              />
              <StatLine
                label="Aprovação → importação"
                value={analytics.importacaoP2.tempoMedioImportacaoDias != null ? `${fmtDec(analytics.importacaoP2.tempoMedioImportacaoDias)} dias` : '—'}
              />
              {analytics.importacaoP2.importadasPorMes.slice(-4).map((m) => (
                <StatLine key={m.mes} label={`Importadas em ${fmtMes(m.mes)}`} value={fmtInt(m.qtdOs)} />
              ))}
            </Card>
            <Card>
              <PanelTitle>
                Eficiência
                <HelpTip text="Relações entre custo, estrutura e porte dos eventos: custo por ingresso (custo total ÷ emitidos), técnicos e leitores por mil ingressos, deslocamento por ingresso e custo logístico por equipamento enviado (logística ÷ equipamentos de retorno)." />
              </PanelTitle>
              <StatLine
                label="Custo por ingresso"
                value={k.custoPorIngresso != null ? fmtMoney(k.custoPorIngresso) : '—'}
              />
              <StatLine
                label="Técnicos por mil ingressos"
                value={k.emitidos ? fmtDec((k.tecnicosAlocacoes / k.emitidos) * 1000) : '—'}
              />
              <StatLine
                label="Leitores por mil ingressos"
                value={k.emitidos ? fmtDec((k.leitoresUtilizados / k.emitidos) * 1000) : '—'}
              />
              <StatLine
                label="Deslocamento por ingresso"
                value={k.emitidos ? fmtMoney(analytics.deslocamento.total / k.emitidos) : '—'}
              />
              <StatLine
                label="Logística por equipamento"
                value={analytics.retorno.totalQtd ? fmtMoney(analytics.logistica.total / analytics.retorno.totalQtd) : '—'}
              />
            </Card>
            <Card>
              <PanelTitle>
                Qualidade dos dados
                <HelpTip text="OS que precisam de atenção no preenchimento: sem nenhum custo lançado, com tipo de evento 'a definir' ou com atendimento presencial 'a definir'. Esses vazios distorcem os indicadores — quanto menores, mais confiável a leitura." />
              </PanelTitle>
              <StatLine label="OS sem custos lançados" value={fmtInt(k.osSemCusto)} />
              <StatLine label="Tipo de evento a definir" value={fmtInt(cont.tipo.aDefinir)} />
              <StatLine label="Atendimento a definir" value={fmtInt(cont.atendimento.aDefinir)} />
              <StatLine
                label="OS com custos lançados"
                value={`${fmtInt(k.totalOs - k.osSemCusto)} (${k.totalOs ? Math.round(((k.totalOs - k.osSemCusto) / k.totalOs) * 100) : 0}%)`}
              />
            </Card>
          </div>

          {/* Eventos e lançamentos */}
          <SectionTitle>
            Eventos e lançamentos
            <HelpTip text="Uma linha por OS com o custo por categoria (mesmas regras do resumo da OS). 'Outros' = staff + alimentação + hospedagem. Ao expandir, os lançamentos individuais são buscados da própria OS: técnicos com turnos e cachês, custos diretos, leitores por setor, equipamentos e logística." />
            <span style={{ fontSize: 12.5, fontWeight: 400, color: BD.grayText }}>
              clique em um evento para expandir os lançamentos da OS
            </span>
          </SectionTitle>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th>Nº OS</th><th>Evento</th><th>Data</th>
                    <th style={{ textAlign: 'right' }}>Técnicos</th>
                    <th style={{ textAlign: 'right' }}>Deslocamento</th>
                    <th style={{ textAlign: 'right' }}>Equipamentos</th>
                    <th style={{ textAlign: 'right' }}>Logística</th>
                    <th style={{ textAlign: 'right' }}>Outros</th>
                    <th style={{ textAlign: 'right' }}>Custo total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {eventosPageRows.length === 0 && (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>📭 Nenhuma OS encontrada com esses filtros.</td></tr>
                  )}
                  {eventosPageRows.map((e) => {
                    const ui = STATUS_UI[e.status]
                    const outros = e.categorias.staff + e.categorias.alimentacao + e.categorias.hospedagem
                    const open = openOs.has(e.osId)
                    const drill = drills[e.osId]
                    return (
                      <Fragment key={e.osId}>
                        <tr onClick={() => toggleDrill(e.osId)} style={{ cursor: 'pointer' }}>
                          <td style={{ color: '#9CA3AF', fontSize: 11 }}>{open ? '▼' : '▶'}</td>
                          <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{e.numero}</span></td>
                          <td style={{ fontWeight: 500 }}>
                            {e.evento ?? '—'}
                            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{e.produtor ?? '—'} · {e.praca ?? '—'}</div>
                          </td>
                          <td>{fmtDate(e.dataEvento)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(e.categorias.tecnicos)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(e.categorias.deslocamento)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(e.categorias.equipamentos)}</td>
                          <td style={{ textAlign: 'right' }}>{e.categorias.logistica ? fmtMoney(e.categorias.logistica) : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{outros ? fmtMoney(outros) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(e.custoTotal)}</td>
                          <td>{ui && <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={11} style={{ padding: '0 12px 16px' }}>
                              {drill === 'loading' && (
                                <div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div>
                              )}
                              {drill === 'error' && (
                                <div style={{ textAlign: 'center', padding: 16, color: '#991B1B', fontSize: 13 }}>
                                  Não foi possível carregar os lançamentos desta OS.
                                </div>
                              )}
                              {drill && drill !== 'loading' && drill !== 'error' && <OsDrill detail={drill} />}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 13, color: BD.grayText }}>
              {fmtInt(analytics.eventos.length)} OS · ordenado por custo total
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button $variant="secondary" disabled={eventosPage <= 1} onClick={() => setEventosPage((p) => p - 1)}>
                ‹ Anterior
              </Button>
              <span style={{ fontSize: 13, color: BD.grayText }}>Página {eventosPage} de {eventosLastPage}</span>
              <Button
                $variant="secondary"
                disabled={eventosPage >= eventosLastPage}
                onClick={() => setEventosPage((p) => p + 1)}
              >
                Próxima ›
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default OsAnalytics
